import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SuggestFeatureForm from '../components/forms/SuggestFeatureForm';
import FeatureCommentForm from '../components/forms/FeatureCommentForm';
import { 
  ChevronUp, 
  MessageSquare, 
  Loader2, 
  Sparkles,
  Clock,
  CheckCircle2,
  Rocket,
  Filter,
  MessageCircle,
  Lock,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  'Requested': {
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    icon: MessageCircle,
    description: 'User suggested'
  },
  'Planned': { 
    color: 'bg-blue-100 text-blue-700 border-blue-200', 
    icon: Clock,
    description: 'On our radar'
  },
  'In Progress': { 
    color: 'bg-amber-100 text-amber-700 border-amber-200', 
    icon: Sparkles,
    description: 'Currently building'
  },
  'Coming Soon': { 
    color: 'bg-purple-100 text-purple-700 border-purple-200', 
    icon: Rocket,
    description: 'Almost ready'
  },
  'Launched': { 
    color: 'bg-green-100 text-green-700 border-green-200', 
    icon: CheckCircle2,
    description: 'Now available'
  },
};

const FeatureCard = ({ feature, onVote, isVoting }) => {
  const statusStyle = statusConfig[feature.status] || statusConfig['Planned'];
  const StatusIcon = statusStyle.icon;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg border border-gray-100 transition-all duration-300">
      <div className="flex gap-4">
        {/* Vote Button */}
        <div className="flex flex-col items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onVote(feature.id)}
            disabled={isVoting}
            className="flex flex-col items-center gap-1 h-auto py-3 px-4 border-2 border-gray-200 hover:border-[#B5541B] hover:bg-[#B5541B]/5 transition-all duration-200 group"
          >
            <ChevronUp className="w-5 h-5 text-gray-400 group-hover:text-[#B5541B] transition-colors" />
            <span className="text-lg font-bold text-[#1F1E1E] group-hover:text-[#B5541B]">
              {isVoting ? <Loader2 className="w-4 h-4 animate-spin" /> : feature.votes}
            </span>
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="text-lg font-bold text-[#1F1E1E] leading-tight">
              {feature.name}
            </h3>
            {feature.status && (
              <Badge className={`${statusStyle.color} border shrink-0 flex items-center gap-1`}>
                <StatusIcon className="w-3 h-3" />
                {feature.status}
              </Badge>
            )}
          </div>
          
          {feature.description && (
            <p className="text-[#3B3535]/70 text-sm leading-relaxed mb-2">
              {feature.description}
            </p>
          )}

          {/* Benefits */}
          {feature.benefits && (
            <p className="text-green-700 bg-green-50 text-xs px-3 py-1.5 rounded-lg mb-3 inline-block">
              ✓ {feature.benefits}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 text-sm">
            {/* User Types (multi-select) */}
            {feature.user_types && feature.user_types.length > 0 && (
              feature.user_types.map((userType, index) => (
                <span key={index} className="text-purple-700 bg-purple-50 px-2 py-1 rounded-full text-xs font-medium">
                  {userType}
                </span>
              ))
            )}
            
            {/* Category */}
            {feature.category && (
              <span className="text-[#B5541B] bg-[#B5541B]/10 px-2 py-1 rounded-full text-xs font-medium">
                {feature.category}
              </span>
            )}
            
            {/* Comment Button */}
            <FeatureCommentForm 
              feature={feature}
              trigger={
                <button 
                  data-testid="feature-comment-button"
                  className="flex items-center gap-1 text-gray-500 hover:text-[#B5541B] transition-colors ml-auto"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Comment</span>
                </button>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureBacklogPage = () => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [votingFeatureId, setVotingFeatureId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    const access = localStorage.getItem('resiax_access');
    if (access === 'granted') {
      setHasAccess(true);
      fetchFeatures();
    } else {
      setIsLoading(false);
    }
    window.scrollTo(0, 0);
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/features`);
      if (!response.ok) throw new Error('Failed to fetch features');
      const data = await response.json();
      setFeatures(data.features);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVote = async (featureId) => {
    setVotingFeatureId(featureId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/features/${featureId}/vote`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to vote');
      const data = await response.json();
      
      // Update the local state with new vote count
      setFeatures(features.map(f => 
        f.id === featureId ? { ...f, votes: data.new_vote_count } : f
      ));
    } catch (err) {
      console.error('Vote failed:', err);
    } finally {
      setVotingFeatureId(null);
    }
  };

  // Get unique statuses for filtering
  const statuses = ['all', ...new Set(features.map(f => f.status).filter(Boolean))];
  
  // Filter features
  const filteredFeatures = selectedStatus === 'all' 
    ? features 
    : features.filter(f => f.status === selectedStatus);

  // Group features by status for display
  const groupedFeatures = filteredFeatures.reduce((acc, feature) => {
    const status = feature.status || 'Planned';
    if (!acc[status]) acc[status] = [];
    acc[status].push(feature);
    return acc;
  }, {});

  // Order of status display
  const statusOrder = ['In Progress', 'Coming Soon', 'Planned', 'Requested', 'Launched'];

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      
      <main className="pt-24 pb-20">
        {!hasAccess ? (
          /* Gated State */
          <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-[#B5541B]/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-[#B5541B]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">
                Feature Backlog
              </h1>
              <p className="text-[#3B3535]/70 mb-8 leading-relaxed">
                The backlog unlocks when you register for a pilot — vote on what matters most
                and help shape what we build next.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  data-testid="unlock-resident-pilot-btn"
                  onClick={() => navigate('/resident-pilot')}
                  className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-5 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group w-full"
                >
                  Register for the resident pilot
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  data-testid="unlock-provider-pilot-btn"
                  onClick={() => navigate('/providers')}
                  variant="outline"
                  className="border-2 border-[#1F1E1E]/20 text-[#1F1E1E] hover:border-[#B5541B] hover:text-[#B5541B] font-semibold h-auto px-8 py-5 text-base rounded-full w-full"
                >
                  Register for the provider pilot
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              <button
                data-testid="unlock-survey-link"
                onClick={() => navigate('/updates#survey', { state: { openSurvey: true } })}
                className="text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors mt-5"
              >
                Not ready to register? Share your experience instead (2 mins) →
              </button>
              <p className="text-xs text-[#3B3535]/50 mt-3">
                Residents and housing professionals both welcome
              </p>
            </div>
          </div>
        ) : (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-6">
              <Rocket className="w-5 h-5 text-[#B5541B]" />
              <span className="text-sm font-semibold text-[#B5541B] uppercase tracking-wide">Feature Backlog</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1E1E] mb-4">
              What We're Building
            </h1>
            <p className="text-lg text-[#3B3535]/70 max-w-2xl mx-auto">
              Vote for the features you want most. Your input shapes our roadmap. 
              Click to upvote or comment on any feature.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-8 justify-center">
            <Filter className="w-4 h-4 text-[#3B3535]/50" />
            {statuses.map(status => (
              <Button
                key={status}
                variant={selectedStatus === status ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedStatus(status)}
                className={selectedStatus === status 
                  ? "bg-[#B5541B] hover:bg-[#8B3F14] text-white" 
                  : "border-gray-200 text-[#3B3535] hover:border-[#B5541B] hover:text-[#B5541B]"
                }
              >
                {status === 'all' ? 'All Features' : status}
              </Button>
            ))}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-[#B5541B] mx-auto mb-4" />
              <p className="text-[#3B3535]/70">Loading features...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-20">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={fetchFeatures} variant="outline">
                Try Again
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && features.length === 0 && (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <Sparkles className="w-12 h-12 text-[#B5541B]/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#1F1E1E] mb-2">No features yet</h3>
              <p className="text-[#3B3535]/70 mb-6">Be the first to suggest a feature!</p>
              <SuggestFeatureForm onFeatureAdded={fetchFeatures} />
            </div>
          )}

          {/* Features List - Grouped by Status */}
          {!isLoading && !error && features.length > 0 && (
            <div className="space-y-10">
              {statusOrder.map(status => {
                const statusFeatures = groupedFeatures[status];
                if (!statusFeatures || statusFeatures.length === 0) return null;
                
                const statusStyle = statusConfig[status] || statusConfig['Planned'];
                const StatusIcon = statusStyle.icon;
                
                return (
                  <div key={status}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${statusStyle.color}`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <h2 className="text-xl font-bold text-[#1F1E1E]">{status}</h2>
                      <span className="text-sm text-[#3B3535]/50">
                        {statusStyle.description}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {statusFeatures
                        .sort((a, b) => b.votes - a.votes)
                        .map(feature => (
                          <FeatureCard
                            key={feature.id}
                            feature={feature}
                            onVote={handleVote}
                            isVoting={votingFeatureId === feature.id}
                          />
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-[#B5541B] to-[#8B3F14] rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-2">Have a feature request?</h3>
              <p className="opacity-90 mb-4">
                Suggest a feature and we'll add it to our backlog. Your idea could shape the platform!
              </p>
              <SuggestFeatureForm onFeatureAdded={fetchFeatures} />
            </div>
          </div>
        </div>
        )}
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default FeatureBacklogPage;
